export interface SSEEventHandler {
  (data: any): void;
}

export interface SSEOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onMessage?: SSEEventHandler;
  onTimeEntryCreated?: SSEEventHandler;
  onNotificationCreated?: SSEEventHandler;
  onNotificationMarkedAsRead?: SSEEventHandler;
  onAllNotificationsMarkedAsRead?: SSEEventHandler;
  onNotificationDeleted?: SSEEventHandler;
}

export class SSEClient {
  private eventSource: EventSource | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectDelay: number = 2000; // 2 segundos iniciales
  private maxReconnectDelay: number = 30000; // 30 segundos máximo
  private handlers: SSEOptions = {};
  private token: string | null = null;

  constructor(private readonly url: string) {}

  /**
   * Conectar al servidor SSE
   */
  connect(options: SSEOptions = {}) {
    this.handlers = { ...this.handlers, ...options };

    if (!this.token) {
      this.token = this.getTokenFromStorage();
    }

    if (!this.token) {
      console.error('❌ SSE: No hay token de autenticación');
      return;
    }

    // Si ya estamos conectados o conectando, solo actualizar los handlers
    if (this.eventSource && (this.eventSource.readyState === EventSource.OPEN || this.eventSource.readyState === EventSource.CONNECTING)) {
            return;
    }

    // Si hay un EventSource existente pero está cerrado, cerrarlo primero
    if (this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
            this.eventSource.close();
      this.eventSource = null;
    }

    // Construir URL con token como query param
    const urlWithToken = `${this.url}?token=${encodeURIComponent(this.token)}`;

    
    try {
      this.eventSource = new EventSource(urlWithToken);

      // Configurar handlers de eventos
      this.setupEventHandlers();

      // Heartbeat
      this.setupHeartbeat();

    } catch (error) {
      console.error('❌ SSE: Error al crear EventSource:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Configurar handlers de eventos
   */
  private setupEventHandlers() {
    if (!this.eventSource) return;

    // Conexión establecida
    this.eventSource.addEventListener('connected', (event: MessageEvent) => {
            this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 2000;

      JSON.parse(event.data);

      if (this.handlers.onConnect) {
        this.handlers.onConnect();
      }
    });

    // Fichaje creado
    this.eventSource.addEventListener('timeEntryCreated', (event: MessageEvent) => {
            try {
        const data = JSON.parse(event.data);
        if (this.handlers.onTimeEntryCreated) {
          this.handlers.onTimeEntryCreated(data);
        }
      } catch (error) {
        console.error('❌ SSE: Error al parsear timeEntryCreated:', error);
      }
    });

    // Notificación creada
    this.eventSource.addEventListener('notificationCreated', (event: MessageEvent) => {
            try {
        const data = JSON.parse(event.data);
        if (this.handlers.onNotificationCreated) {
          this.handlers.onNotificationCreated(data);
        }
      } catch (error) {
        console.error('❌ SSE: Error al parsear notificationCreated:', error);
      }
    });

    // Notificación marcada como leída
    this.eventSource.addEventListener('notificationMarkedAsRead', (event: MessageEvent) => {
            try {
        const data = JSON.parse(event.data);
        if (this.handlers.onNotificationMarkedAsRead) {
          this.handlers.onNotificationMarkedAsRead(data);
        }
      } catch (error) {
        console.error('❌ SSE: Error al parsear notificationMarkedAsRead:', error);
      }
    });

    // Todas las notificaciones marcadas como leídas
    this.eventSource.addEventListener('allNotificationsMarkedAsRead', (event: MessageEvent) => {
            try {
        const data = JSON.parse(event.data);
        if (this.handlers.onAllNotificationsMarkedAsRead) {
          this.handlers.onAllNotificationsMarkedAsRead(data);
        }
      } catch (error) {
        console.error('❌ SSE: Error al parsear allNotificationsMarkedAsRead:', error);
      }
    });

    // Notificación eliminada
    this.eventSource.addEventListener('notificationDeleted', (event: MessageEvent) => {
            try {
        const data = JSON.parse(event.data);
        if (this.handlers.onNotificationDeleted) {
          this.handlers.onNotificationDeleted(data);
        }
      } catch (error) {
        console.error('❌ SSE: Error al parsear notificationDeleted:', error);
      }
    });

    // Error
    this.eventSource.onerror = (error: Event) => {
      this.isConnected = false;

      if (this.handlers.onError) {
        this.handlers.onError(error);
      }

      // Programar reconexión
      this.scheduleReconnect();
    };
  }

  /**
   * Configurar heartbeat
   */
  private setupHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.token) {
        // El servidor envía keep-alive, no necesitamos enviar nada
        // Solo verificamos que la conexión sigue activa
        if (this.eventSource && this.eventSource.readyState === EventSource.CLOSED) {
                    this.scheduleReconnect();
        }
      }
    }, 30 * 1000); // Cada 30 segundos
  }

  /**
   * Programar reconexión
   */
  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('🔴 SSE: Máximo de intentos de reconexión alcanzado');
      return;
    }

    // Limpiar reconexión anterior
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay, this.maxReconnectDelay);

    this.reconnectTimeout = setTimeout(() => {
      this.connect(this.handlers);
    }, delay);

    // Exponential backoff
    this.reconnectDelay = this.reconnectDelay * 2;
  }

  /**
   * Desconectar del servidor SSE
   */
  disconnect() {
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.isConnected = false;

    if (this.handlers.onDisconnect) {
      this.handlers.onDisconnect();
    }
  }

  /**
   * Obtener token de autenticación del localStorage
   */
  private getTokenFromStorage(): string | null {
    try {
      const stored = localStorage.getItem('compilatime-auth');
      if (stored) {
        const parsedData = JSON.parse(stored);
        return parsedData.token || null;
      }
    } catch (error) {
      console.error('❌ SSE: Error al leer token del localStorage:', error);
    }
    return null;
  }
}
